/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useNotifications } from '@/data/Content/Notifications';
import { useOrganizationDetails } from '@/data/Content/OrganizationDetails';
import { contactCreator, contactUpdater, usePersonContact } from '@/data/Content/PersonContact';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import {
	DATA_KEY_BANAMEX_EVO_OPTIONS_INQUIRY,
	DATA_KEY_PAYMENT_INFO,
} from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { BILLING_ADDRESS_ID, UNSUPPORTED_FOR_MULTI } from '@/data/constants/payment';
import { EditableAddress } from '@/data/types/Address';
import { ID, TransactionErrorResponse } from '@/data/types/Basic';
import { Order, PaymentInfo, PaymentInstruction, PaymentToEdit } from '@/data/types/Order';
import { PaymentCardAction } from '@/data/types/PaymentCard';
import { RequestQuery } from '@/data/types/RequestQuery';
import { filterUnSupportedPayments } from '@/data/utils/filterUnSupportedPayment';
import { dAdd, dFix, dMul } from '@/data/utils/floatingPoint';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { personalContactInfoMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personalContactInfoMutatorKeyMatcher';
import { getPaymentToEdit, markSinglePaymentDirtyIfNeeded } from '@/data/utils/payment';
import { processError } from '@/data/utils/processError';
import {
	transactionsCart,
	transactionsPaymentInstruction,
} from 'integration/generated/transactions';
import {
	CartUsablePaymentInfo,
	CartUsablePaymentInformation,
} from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { Dictionary, keyBy, pickBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR, { KeyedMutator, useSWRConfig } from 'swr';
import { useUser } from '../User';
import { preCheckout } from './CheckOut';
export { getPaymentToEdit, markSinglePaymentDirtyIfNeeded };

const usablePaymentInfoDataMap = ({ usablePaymentInformation = [] }: CartUsablePaymentInfo) =>
	filterUnSupportedPayments(usablePaymentInformation);

const usablePaymentInfoFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		params: RequestParams
	) =>
		usablePaymentInfoDataMap(
			await transactionsCart(pub).cartGetUsablePaymentInfo(storeId, query, params)
		);

export const calculateOrder =
	(pub: boolean) =>
	async (
		storeId: string,
		orderId: string,
		query: RequestQuery = {},
		data: any,
		params: RequestParams = {}
	) =>
		await transactionsCart(pub).cartCalculateOrder2(storeId, orderId, query, data, params);

export const addPaymentInstructionFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: any,
		// use any, since the spec not good
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionAddPaymentInstruction(
			storeId,
			query,
			data,
			params
		);

export const deleteAllPaymentInstructionFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionDeleteAllPaymentInstructions(
			storeId,
			query,
			params
		);

const paymentInstructionRemover =
	(pub: boolean) =>
	async (storeId: string, pi: PaymentInfo, query: RequestQuery = {}, params: RequestParams) =>
		await transactionsPaymentInstruction(pub).paymentInstructionDeletePaymentInstruction(
			storeId,
			pi.piId as string,
			query,
			params
		);

const paymentInstructionUpdater =
	(pub: boolean) =>
	async (
		storeId: string,
		data: any,
		// use any, since the spec not good
		query: RequestQuery = {},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionUpdatePaymentInstruction(
			storeId,
			query,
			data,
			params
		);

const canBeUpdated = (
	payment: PaymentToEdit,
	paymentInstruction: PaymentInstruction[],
	usableByPolicy: Dictionary<CartUsablePaymentInformation>
) => {
	let rc = !!payment.piId;
	if (rc) {
		const old = paymentInstruction.find((p) => p.piId === payment.piId) as PaymentInstruction;
		rc =
			!UNSUPPORTED_FOR_MULTI[usableByPolicy[payment.policyId ?? ''].paymentMethodName as string] &&
			!UNSUPPORTED_FOR_MULTI[old.payMethodId];
	}
	return rc;
};

const paymentInstructionSendClubMsgCmd =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			orderId: string;
			card_num: string;
		},
		query: RequestQuery = {},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionSendClubMsgCmd(
			storeId,
			query,
			data,
			params
		);

const paymentInstructionCreatePaypalOrder =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			orderId: string;
		},
		query: RequestQuery = {},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionCreatePaypalOrder(
			storeId,
			query,
			data,
			params
		);

const paymentInstructionBanamexEvoGetOptionsInquiry =
	(pub: boolean) =>
	async (storeId: string, orderId: string, query: RequestQuery = {}, params: RequestParams) =>
		await transactionsPaymentInstruction(pub).paymentInstructionBanamexEvoGetOptionsInquiry(
			storeId,
			{
				orderId,
				...query,
			},
			params
		);

const paymentInstructionBanamexEvoGetCards =
	(pub: boolean) =>
	async (storeId: string, userId: string, query: RequestQuery = {}, params: RequestParams) =>
		await transactionsPaymentInstruction(pub).paymentInstructionBanamexEvoGetCards(
			storeId,
			{
				userId,
				...query,
			},
			params
		);

const paymentInstructionBanamexEvoUpdateSession =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			sessionId: string;
			amount: string;
			orderId: string;
			numberOfPayments: string;
			planId?: string;
			saveTokenCard: boolean;
		},
		query: RequestQuery = {},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionBanamexEvoUpdateSession(
			storeId,
			data,
			query,
			params
		);

const paymentInstructionBanamexEvoCreateSessionWithToken =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			memberId: string;
			amount: string;
			orderId: string;
			transactionId?: string;
			planId?: string;
			numberOfPayments: string;
			token: string;
			securityCode: string;
		},
		query: RequestQuery = {},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionBanamexEvoCreateSessionWithToken(
			storeId,
			data,
			query,
			params
		);

const paymentInstructionDpValeElectronico =
	(pub: boolean) =>
	async (
		storeId: string,
		data: {
			orderId: string;
			returnMsg: unknown;
		},
		query: RequestQuery = {},
		params: RequestParams
	) =>
		await transactionsPaymentInstruction(pub).paymentInstructionDpValeElectronico(
			storeId,
			data,
			query,
			params
		);

export enum PAYMENTS_IDS {
	BANAMEX_EVO = 'BanamexEvo',
	MERCADO_PAGO = 'MercadoPagoMSI',
	PAYPAL_EXPRESS = 'PayPalExpress',
	APLAZO = 'Aplazo',
	COPPEL_PAY = 'CoppelPay',
	EFECTIVO = 'PagoEfectivo',
	DPVALE_ELECTRONICO = 'DpValeElectronico',
	TARJETA_CLUB = 'TarjetaClub',
	DP_VALE = 'DpVale',
	CLUB_DP_PUNTOS = 'ClubDPPuntos',
}

export const usePaymentBanamexEvoGetOptionsInquiry = (orderId?: string) => {
	const params = useExtraRequestParameters();
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);

	const {
		data: optionsInquiry,
		error: optionsInquiryError,
		mutate: mutateOptionsInquiry,
	} = useSWR(
		storeId && orderId
			? [{ storeId, orderId, query: { langId } }, DATA_KEY_BANAMEX_EVO_OPTIONS_INQUIRY]
			: null,
		async ([{ storeId, orderId, query }]) =>
			paymentInstructionBanamexEvoGetOptionsInquiry(true)(storeId, orderId, query, params)
	);

	return {
		optionsInquiry,
		paymentPlanOffer: optionsInquiry?.data?.paymentPlanOffer ?? [],
		optionsInquiryError,
		mutateOptionsInquiry,
	};
};

export const usePaymentBanamexEvoGetCards = () => {
	const params = useExtraRequestParameters();
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const { user } = useUser();

	const userId = user?.userId;

	const {
		data: banamexEvoCards,
		error: banamexEvoCardsError,
		mutate: mutateBanamexEvoCards,
		isLoading: banamexEvoCardsLoading,
	} = useSWR(
		storeId && userId
			? [{ storeId, userId, query: { langId } }, DATA_KEY_BANAMEX_EVO_OPTIONS_INQUIRY]
			: null,
		async ([{ storeId, userId, query }]) =>
			paymentInstructionBanamexEvoGetCards(true)(storeId, userId, query, params)
	);

	return {
		banamexEvoCards: banamexEvoCards?.data?.cards ?? [],
		banamexEvoCardsError,
		banamexEvoCardsLoading,
		mutateBanamexEvoCards,
	};
};

export const usePayment = (
	cart: Order,
	_mutateCart?: KeyedMutator<Order>,
	removeCleaning?: boolean
) => {
	const { mutate } = useSWRConfig();
	const router = useNextRouter();
	const cardText = useLocalization('AddressCard');
	const paymentCard = useLocalization('PaymentInfoCard');
	const { notifyError, showSuccessMessage } = useNotifications();
	const success = useLocalization('success-message');
	const { settings } = useSettings();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const [isCleaning, setIsCleaning] = useState(true);
	const { user } = useUser();

	const onCleanPaymentInstruction = useCallback(async () => {
		try {
			await deleteAllPaymentInstructionFetcher(true)(storeId, { langId }, params);

			await calculateOrder(true)(
				storeId,
				cart.orderId,
				undefined,
				{
					calculationUsageId: ['-1', '-2', '-3', '-4', '-5', '-6', '-7'],
				},
				params
			);

			await preCheckout(true)(storeId, undefined, params);

			await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			setIsCleaning(false);
		}
	}, [langId, params, storeId, notifyError, mutate, cart.orderId]);

	useEffect(() => {
		if (removeCleaning) {
			setIsCleaning(false);
			return;
		}
		onCleanPaymentInstruction();
	}, [onCleanPaymentInstruction, removeCleaning]);

	const {
		data: usablePayments,
		error: usablePaymentError,
		mutate: mutateUsablePayment,
	} = useSWR(
		storeId
			? [{ storeId, query: { langId, allPaymentMethods: true } }, DATA_KEY_PAYMENT_INFO]
			: null,
		async ([{ storeId, query }]) => usablePaymentInfoFetcher(true)(storeId, query, params)
	);
	const usableByPolicy = useMemo(() => keyBy(usablePayments, 'xumet_policyId'), [usablePayments]);
	const [methodError, setMethodError] = useState<Record<string, any>>();

	/**
	 * ==================== address ===================
	 */

	const { billingAddress: personals } = usePersonContact();
	const { org, parentOrg } = useOrganizationDetails();
	const billingAddress = useMemo(
		() => [...personals, ...org.addresses, ...parentOrg.addresses],
		[personals, org, parentOrg]
	);

	// converting to an object with nickName as key
	const billingAddressMap = useMemo(() => keyBy(billingAddress, 'nickName'), [billingAddress]);

	const toggleEditCreateAddress = useCallback(
		(address: EditableAddress | null) => () => {
			setAddressToEdit(address);
		},
		[]
	);

	/**
	 * addressToEdit and editableAddress are basically same, only differ by addressId if it is existing address.
	 * if addressId exist in addressToEdit, means we are updating, otherwise we are adding a new address
	 */
	const [addressToEdit, setAddressToEdit] = useState<EditableAddress | null>(null);
	const editableAddress = useMemo<EditableAddress | null>(
		() =>
			addressToEdit
				? (pickBy(addressToEdit, (_value, key) => key !== 'addressId') as EditableAddress)
				: null,
		[addressToEdit]
	);
	const onAddressEditOrCreate = useCallback(
		(onSelect?: (name: keyof PaymentToEdit, _addressId: string) => void) =>
			async (address: EditableAddress) => {
				const { addressLine1, addressLine2, nickName, ..._address } = address;

				let addressId = '';
				let msgKey: keyof typeof success;
				try {
					if (addressToEdit?.addressId) {
						msgKey = 'EDIT_ADDRESS_SUCCESS';
						// if addressToEdit has addressId, means update, create otherwise.
						const data = { addressLine: [addressLine1, addressLine2 ?? ''], ..._address };
						const res = await contactUpdater(true)(
							settings?.storeId ?? '',
							nickName,
							undefined,
							data,
							params
						);
						addressId = res?.addressId ?? '';
					} else {
						msgKey = 'ADD_ADDRESS_SUCCESS';
						const data = { addressLine: [addressLine1, addressLine2 ?? ''], nickName, ..._address };
						const res = await contactCreator(true)(
							settings?.storeId ?? '',
							undefined,
							data,
							params
						);
						addressId = res?.addressId ?? '';
					}
					mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
					onSelect && onSelect(BILLING_ADDRESS_ID, addressId);
					showSuccessMessage(success[msgKey].t([address.nickName]));
					setAddressToEdit(null);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			},
		[
			addressToEdit?.addressId,
			mutate,
			notifyError,
			params,
			settings?.storeId,
			showSuccessMessage,
			success,
		]
	);

	const getBillingAddressCardActions = (
		address: EditableAddress,
		selectedAddressId?: string,
		validAddress = true,
		onSelect?: (name: keyof PaymentToEdit, _addressId: string) => void
	) => {
		const { addressId, addressLine1 } = address;
		const isSelected = addressId === selectedAddressId;
		// only offer edit for invalid address
		// since invalid addresses are not selected at shipping
		return [
			!validAddress && {
				text: cardText.EditButton.t(),
				handleClick: toggleEditCreateAddress(address),
			},
			!isSelected &&
				addressId &&
				addressLine1 && {
					text: cardText.UseAddress.t(),
					handleClick: () => onSelect && onSelect(BILLING_ADDRESS_ID, addressId),
					variant: 'outlined',
				},
		].filter(Boolean);
	};

	/**
	 * =============== payment instruction ======================
	 */

	const paymentInstruction = useMemo(() => cart.paymentInstruction ?? [], [cart]);

	/**
	 * states for multiple payments
	 *
	 * Multiple payments: we should be able to update PI one by one instead of deleting all and add new, so that
	 * for untouched create PI, shopper do not need to type in again.
	 *
	 */
	const [paymentNumberToEdit, setPaymentNumberToEdit] = useState<number | null>(null);
	const [paymentsToEdit, setPaymentsToEdit] = useState<PaymentToEdit[]>(
		paymentInstruction.map(getPaymentToEdit)
	);

	const constructPI = (data: PaymentToEdit): PaymentInfo => {
		const {
			piId,
			policyId,
			billing_address_id,
			account,
			piAmount,
			numberOfInstallments,
			dp_card_number,
			prom_val,
			txt_num,
			sessionid,
			issuer,
			paymentMethodId,
			x_allowMultiplePI,
			payMethodId: _payMethodId = '',
		} = data;
		const { paymentMethodName: payMethodId = _payMethodId } =
			usablePayments?.find((payment) => payment.xumet_policyId === policyId) ?? {};
		let pi: PaymentInfo = {
			policyId,
			payMethodId,
			billing_address_id,
			piAmount,
			x_allowMultiplePI,
		};

		switch (payMethodId) {
			case PAYMENTS_IDS.BANAMEX_EVO: {
				pi = {
					...pi,
					sessionid,
					numberOfInstallments,
				};
				break;
			}
			case PAYMENTS_IDS.MERCADO_PAGO: {
				pi = {
					...pi,
					account,
					issuer,
					numberOfInstallments,
					paymentMethodId,
				};
				break;
			}
			case PAYMENTS_IDS.TARJETA_CLUB: {
				pi = {
					...pi,
					card_num: dp_card_number,
					numberOfInstallments,
					prom_val,
					txt_num,
				};
				break;
			}
			default:
				break;
		}

		if (piId) {
			pi.piId = piId;
		}

		return pi;
	};

	const paymentsTotal = useMemo(
		() => paymentsToEdit.reduce((tally, payment) => dAdd(tally, payment.piAmount), 0),
		[paymentsToEdit]
	);
	const cartTotal = useMemo(() => dFix(cart?.grandTotal ?? 0), [cart]);

	useEffect(() => {
		setPaymentsToEdit(paymentInstruction.map(getPaymentToEdit));
	}, [paymentInstruction]);

	const onSinglePayment = async (data: PaymentToEdit | Array<PaymentToEdit>) => {
		try {
			await deleteAllPaymentInstructionFetcher(true)(storeId, { langId }, params);
			const pi = Array.isArray(data) ? data.map(constructPI) : [constructPI(data)];

			await addPaymentInstructionFetcher(true)(
				storeId,
				{ langId },
				{ paymentInstruction: [...pi] },
				params
			);
			return true;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return false;
		}
	};

	const onReCalculate = async () => {
		try {
			await calculateOrder(true)(
				storeId,
				cart.orderId,
				undefined,
				{
					calculationUsageId: ['-1', '-2', '-3', '-4', '-5', '-6', '-7'],
				},
				params
			);

			await preCheckout(true)(storeId, undefined, params);

			return await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	const onSinglePaymentSubmit = async (data?: PaymentToEdit | Array<PaymentToEdit>) => {
		try {
			await deleteAllPaymentInstructionFetcher(true)(storeId, { langId }, params);

			if (data) {
				const pi = Array.isArray(data) ? data.map(constructPI) : [constructPI(data)];
				await addPaymentInstructionFetcher(true)(
					storeId,
					{ langId },
					{ paymentInstruction: [...pi] },
					params
				);
			}

			await calculateOrder(true)(
				storeId,
				cart.orderId,
				undefined,
				{
					calculationUsageId: ['-1', '-2', '-3', '-4', '-5', '-6', '-7'],
				},
				params
			);

			await preCheckout(true)(storeId, undefined, params);

			return await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	const onMultiDeleteSingle = async (paymentNumber: number) => {
		const storeId = settings?.storeId ?? '';
		try {
			await paymentInstructionRemover(true)(
				storeId,
				paymentsToEdit[paymentNumber],
				undefined,
				params
			);
			setPaymentNumberToEdit(null);
			await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		}
	};

	const onMultiCreateOrEditSingle = async (data: PaymentToEdit) => {
		if (!data.dirty === false) {
			// existing
			const pi = constructPI(data);
			try {
				const canUpdate = canBeUpdated(data, paymentInstruction, usableByPolicy);
				if (pi.piId && canUpdate) {
					await paymentInstructionUpdater(true)(
						storeId,
						{ paymentInstruction: [pi] },
						undefined,
						params
					);
				} else {
					if (pi.piId) {
						// need to delete and recreate
						await paymentInstructionRemover(true)(storeId, data, undefined, params);
					}
					// create
					await addPaymentInstructionFetcher(true)(
						storeId,
						{ langId },
						{ paymentInstruction: [pi] },
						params
					);
				}
				setPaymentNumberToEdit(null);
				await mutate(cartMutatorKeyMatcher(EMPTY_STRING), undefined);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		} else {
			setPaymentNumberToEdit(null);
		}
	};

	const getPaymentCardActions = (paymentNumber: number): PaymentCardAction[] => [
		{
			text: paymentCard.Actions.Edit.t(),
			handleClick: () => setPaymentNumberToEdit(paymentNumber),
		},
		{
			text: paymentCard.Actions.Delete.t(),
			handleClick: () => onMultiDeleteSingle(paymentNumber),
			enableConfirmation: true,
		},
	];

	/**
	 * Gets the maximum amount allowed for this payment method based on the running total of other payments
	 */
	const getMaximumPiAmount = (paymentNumber: number) => {
		let rc: number = dFix(cart.grandTotal);
		if (paymentInstruction.length >= 1) {
			const otherPayments: PaymentInstruction[] = paymentInstruction.slice();
			otherPayments.splice(paymentNumber, 1);
			rc = dAdd(rc, dMul(-1, getRunningPaymentTotal(otherPayments)));
		}
		return rc;
	};

	/**
	 * Gets the total piAmount from the specified payment list
	 * @returns Total piAmount
	 */
	const getRunningPaymentTotal = (paymentList: PaymentInstruction[]) => {
		const rc = paymentList?.length > 0 ? dAdd(...paymentList.map((p) => p.piAmount)) : 0;
		return rc;
	};

	const validateMulti = useCallback(
		(multi: boolean, payment: PaymentToEdit) => {
			setMethodError(undefined);
			let valid = true;

			if (multi) {
				const others = paymentInstruction.filter((pi) => !payment.piId || pi.piId !== payment.piId);
				const all = [...others.map(({ xpaym_policyId }) => xpaym_policyId), payment.policyId];
				const firstInvalid = all.find(
					(policyId) =>
						UNSUPPORTED_FOR_MULTI[usableByPolicy[policyId ?? ''].paymentMethodName as string]
				);
				if (others.length > 0 && !!firstInvalid) {
					valid = false;
					const methodName = usableByPolicy[firstInvalid].description;
					setMethodError(() => ({ methodName }));
				}
			}
			return valid;
		},
		[paymentInstruction, usableByPolicy]
	);

	const onSendClubMsgCmd = async (card_num: string) => {
		try {
			const response = await paymentInstructionSendClubMsgCmd(true)(
				storeId,
				{
					card_num,
					orderId: cart.orderId,
				},
				{ langId },
				params
			);
			return response;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	const onCreatePaypalOrder = async () => {
		try {
			const response = await paymentInstructionCreatePaypalOrder(true)(
				storeId,
				{
					orderId: cart.orderId,
				},
				{ langId },
				params
			);
			return response;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	const onBanamexEvoUpdateSession = async (data: {
		orderId: string;
		sessionId: string;
		amount: string;
		numberOfPayments: string;
		planId?: string;
		saveTokenCard: boolean;
	}) => {
		try {
			const response = await paymentInstructionBanamexEvoUpdateSession(true)(
				storeId,
				data,
				{ langId },
				params
			);
			return response;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	const onBanamexEvoCreateSessionWithToken = async (data: {
		amount: string;
		orderId: string;
		transactionId?: string;
		planId?: string;
		numberOfPayments: string;
		token: string;
		securityCode: string;
	}) => {
		try {
			const memberId = user?.userId ?? '';

			const response = await paymentInstructionBanamexEvoCreateSessionWithToken(true)(
				storeId,
				{
					memberId,
					...data,
				},
				{ langId },
				params
			);
			return response;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	const onDpValeElectronico = async (returnMsg: unknown) => {
		try {
			const response = await paymentInstructionDpValeElectronico(true)(
				storeId,
				{
					orderId: cart.orderId,
					returnMsg,
				},
				{ langId },
				params
			);
			return response;
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			return null;
		}
	};

	return {
		paymentsToEdit,
		usablePayments,
		billingAddressMap,
		paymentNumberToEdit,
		setPaymentNumberToEdit,
		cart,
		getBillingAddressCardActions,
		onAddressEditOrCreate,
		addressToEdit,
		editableAddress,
		toggleEditCreateAddress,
		usablePaymentError,
		mutateUsablePayment,
		onSinglePayment,
		onSinglePaymentSubmit,
		getPaymentCardActions,
		cartTotal,
		paymentsTotal,
		onMultiCreateOrEditSingle,
		onMultiDeleteSingle,
		getMaximumPiAmount,
		validateMulti,
		methodError,
		setMethodError,
		onCleanPaymentInstruction,
		onSendClubMsgCmd,
		onCreatePaypalOrder,
		onBanamexEvoUpdateSession,
		onBanamexEvoCreateSessionWithToken,
		onDpValeElectronico,
		onReCalculate,
		isCleaning,
	};
};
